// Inbox — Mo's "no-reply emails from last 7 days" surface.
//
// Wired to three Lambdas:
//   • moreece-inbox-list  — pulls Mo's inbound emails (last 7d) from
//     signals_items where Mo hasn't replied. Owner_pin = 2693q only.
//   • moreece-inbox-tidy  — claude-haiku-4-5 with Mo's voice rules
//     baked into the system prompt.
//   • moreece-inbox-draft — Microsoft Graph createReply → PATCH body
//     with Mo's reply + signature; saves to Outlook Drafts. Mo opens
//     Outlook to review/send. NEVER auto-sends.

import { useCallback, useEffect, useMemo, useState } from 'react';

const SANS = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

const LIST_URL  = import.meta.env.VITE_AWS_INBOX_LIST_URL;
const TIDY_URL  = import.meta.env.VITE_AWS_INBOX_TIDY_URL;
const DRAFT_URL = import.meta.env.VITE_AWS_INBOX_DRAFT_URL;

// Mo's ask 2026-05-10: don't re-hit inbox-list every time the page is
// opened — the Lambda does a fan-out across signals_items chunks and a
// few seconds of latency on every revisit is wasted. Cache the response
// in sessionStorage for 30 min; the Refresh button forces a fresh fetch.
const CACHE_TTL_MS = 30 * 60 * 1000;
const CACHE_KEY    = 'moreece.inbox.list.cache.v1';

function readCache(userPin) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userPin !== userPin) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    if (!Array.isArray(parsed.emails)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(userPin, emails) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      userPin, emails, fetchedAt: Date.now(),
    }));
  } catch {
    // sessionStorage quota / disabled — silent, not worth surfacing.
  }
}

function clearCache() {
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

function fmtRelative(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

export default function Inbox({ session }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  // When we serve from sessionStorage cache, surface the age so Mo
  // knows the data isn't fresh (and can hit Refresh to force a refetch).
  const [cacheAgeMs, setCacheAgeMs] = useState(null);
  // openId = which email is currently being replied to (null = none).
  const [openId, setOpenId] = useState(null);
  // Per-email reply state. Map of id → draft text.
  const [drafts, setDrafts] = useState({});
  // Per-email tidy state.
  const [tidying, setTidying] = useState({});
  // Per-email save-to-draft state.
  const [saving, setSaving] = useState({});
  // Per-email error/feedback after save.
  const [feedback, setFeedback] = useState({});

  // force=true bypasses the 30-min cache (used by the Refresh button).
  const loadInbox = useCallback(async (force = false) => {
    if (!session?.code || !LIST_URL) {
      setListError('Inbox endpoint not configured.');
      setLoading(false);
      return;
    }
    if (!force) {
      const cached = readCache(session.code);
      if (cached) {
        setEmails(cached.emails);
        setCacheAgeMs(Date.now() - cached.fetchedAt);
        setLoading(false);
        setListError(null);
        return;
      }
    }
    setLoading(true);
    setListError(null);
    try {
      const res = await fetch(LIST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userPin: session.code }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.emails) ? json.emails : [];
      setEmails(list);
      setCacheAgeMs(0);
      writeCache(session.code, list);
    } catch (e) {
      setListError(e?.message || 'Failed to load inbox');
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [session?.code]);

  useEffect(() => { loadInbox(false); }, [loadInbox]);

  const headline = useMemo(() => {
    const n = emails.length;
    const base = `${n} email${n === 1 ? '' : 's'} awaiting your reply · last 7 days`;
    if (cacheAgeMs == null) return base;
    if (cacheAgeMs < 60_000) return base; // fresh fetch — no need to call it cached
    const m = Math.round(cacheAgeMs / 60_000);
    return `${base} · cached ${m}m ago`;
  }, [emails.length, cacheAgeMs]);

  function setDraft(id, text) {
    setDrafts(d => ({ ...d, [id]: text }));
  }

  // Tidy with Harry — Lambda call to claude-haiku-4-5. The Lambda's
  // system prompt has Mo's voice rules baked in (no em dashes, no
  // banned phrases, short sentences, no greeting/sign-off — those
  // come from the signature on save).
  async function tidyDraft(id) {
    const current = (drafts[id] || '').trim();
    if (!current || !TIDY_URL) return;
    setTidying(t => ({ ...t, [id]: true }));
    setFeedback(f => { const n = { ...f }; delete n[id]; return n; });
    try {
      const email = emails.find(e => e.id === id);
      const res = await fetch(TIDY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userPin: session.code,
          draft: current,
          context: email ? {
            subject: email.subject,
            sender_name: email.from_name,
            sender_email: email.from_email,
            preview: email.preview,
          } : null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      if (json?.tidied) setDrafts(d => ({ ...d, [id]: json.tidied }));
    } catch (e) {
      setFeedback(f => ({ ...f, [id]: { type: 'error', text: `Tidy failed: ${e.message}` } }));
    } finally {
      setTidying(t => { const n = { ...t }; delete n[id]; return n; });
    }
  }

  // Save as draft — Lambda creates an Outlook draft via Graph
  // createReply, patches body with Mo's reply, and Mo's signature
  // is appended server-side. NEVER sends the email; Mo opens Outlook
  // to review and send manually. Row is removed from the inbox after
  // successful draft creation since Mo will follow up from Drafts.
  async function saveAsDraft(id) {
    const text = (drafts[id] || '').trim();
    if (!text || !DRAFT_URL) return;
    const email = emails.find(e => e.id === id);
    if (!email) return;
    setSaving(s => ({ ...s, [id]: true }));
    setFeedback(f => { const n = { ...f }; delete n[id]; return n; });
    try {
      const res = await fetch(DRAFT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userPin: session.code,
          source_id: email.source_id,
          reply_text: text,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        // Lambda returns { error, detail?, code? }. Surface detail when
        // present so a Graph permission hint reaches the UI directly.
        const headline = json?.error || `HTTP ${res.status}`;
        const detail = json?.detail || '';
        const e = new Error(detail ? `${headline}\n\n${detail}` : headline);
        e.code = json?.code;
        throw e;
      }
      // Drop from list — Mo will finish the reply from his Outlook drafts.
      const next = emails.filter(e => e.id !== id);
      setEmails(next);
      // Keep the cache consistent with the in-memory state so a revisit
      // within 30 min doesn't resurrect the saved-as-draft row.
      writeCache(session.code, next);
      setDrafts(d => { const n = { ...d }; delete n[id]; return n; });
      if (openId === id) setOpenId(null);
    } catch (e) {
      setFeedback(f => ({ ...f, [id]: { type: 'error', text: `Save failed: ${e.message}` } }));
    } finally {
      setSaving(s => { const n = { ...s }; delete n[id]; return n; });
    }
  }

  return (
    <div style={{
      width: '100%', padding: '32px 36px',
      fontFamily: SANS, color: '#0F1419',
      boxSizing: 'border-box',
    }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{
            fontFamily: 'Caslon, "Big Caslon", Georgia, serif',
            fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em',
            margin: 0, color: '#0F1419',
          }}>
            Inbox
          </h1>
          <div style={{
            fontSize: 12, color: '#8B9094', marginTop: 6, letterSpacing: '-0.005em',
          }}>
            {loading ? 'Loading…' : headline}
            {' · '}<span style={{ color: '#0F5F2D' }}>your inbox only</span>
            {' · '}<span style={{ color: '#6E7278' }}>save sends to your Outlook Drafts (signature attached)</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { clearCache(); loadInbox(true); }}
          disabled={loading}
          style={{
            padding: '7px 14px', borderRadius: 8,
            border: '1px solid #E0DED6', background: '#fff',
            color: '#3E4148', fontSize: 12.5, fontWeight: 500,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {listError ? (
        <div style={{
          padding: 16, color: '#A03422',
          background: '#FBEAE5', border: '1px solid #F2C9BD', borderRadius: 10,
          marginBottom: 16, fontSize: 13,
        }}>
          {listError}
        </div>
      ) : null}

      {!loading && emails.length === 0 ? (
        <div style={{
          padding: 60, textAlign: 'center', color: '#8B9094',
          background: '#FAFAF8', border: '1px solid #E8E6E0', borderRadius: 12,
        }}>
          Inbox zero. Nothing waiting on you in the last 7 days.
        </div>
      ) : !loading && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {emails.map(e => {
            const isOpen = openId === e.id;
            const draft = drafts[e.id] || '';
            const isTidying = !!tidying[e.id];
            const isSaving = !!saving[e.id];
            const fb = feedback[e.id];
            return (
              <li key={e.id}
                style={{
                  background: '#fff', border: '1px solid #E8E6E0', borderRadius: 10,
                  padding: '14px 16px',
                }}
              >
                <div
                  onClick={() => setOpenId(isOpen ? null : e.id)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0F1419', marginBottom: 2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.subject}
                      </div>
                      <div style={{ fontSize: 12, color: '#6E7278' }}>
                        {e.from_name} · {e.from_email}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: '#9A9B9E', whiteSpace: 'nowrap' }}>
                      {fmtRelative(e.received_at)}
                    </span>
                  </div>
                  {isOpen ? (
                    // Full email body, chunked-and-rejoined server-side.
                    // White-space: pre-wrap preserves paragraph structure
                    // for readability; the chunker had collapsed runs of
                    // whitespace to single spaces but kept word ordering.
                    <div style={{
                      fontSize: 13, color: '#3E4148', lineHeight: 1.55, marginTop: 10,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      maxHeight: 360, overflowY: 'auto',
                      padding: '10px 12px', background: '#FAFAF8',
                      border: '1px solid #ECEAE4', borderRadius: 8,
                    }}>
                      {(e.body && e.body.trim()) || e.preview || '(empty body)'}
                    </div>
                  ) : (
                    <div style={{
                      fontSize: 12.5, color: '#6E7278', lineHeight: 1.45, marginTop: 8,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {e.preview}
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #ECEAE4' }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6E7278', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      Your reply
                    </label>
                    <textarea
                      value={draft}
                      onChange={(ev) => setDraft(e.id, ev.target.value)}
                      placeholder="Type your reply. Click Tidy with Harry to clean it up. Don't add a sign-off — your signature attaches automatically when saved."
                      rows={5}
                      disabled={isTidying || isSaving}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.5,
                        padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #E0DED6', background: '#FAFAF8',
                        resize: 'vertical', minHeight: 100,
                      }}
                    />
                    {fb ? (
                      <div style={{
                        marginTop: 8, padding: '8px 12px', borderRadius: 6,
                        fontSize: 11.5, lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        background: fb.type === 'error' ? '#FBEAE5' : '#E2F1E6',
                        color: fb.type === 'error' ? '#A03422' : '#0F5F2D',
                        border: `1px solid ${fb.type === 'error' ? '#F2C9BD' : '#BBDDC2'}`,
                      }}>
                        {fb.text}
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => tidyDraft(e.id)}
                        disabled={!draft.trim() || isTidying || isSaving}
                        style={{
                          padding: '7px 14px', borderRadius: 8,
                          border: '1px solid #E0DED6', background: '#F4F3EF',
                          color: '#3E4148', fontSize: 12.5, fontWeight: 500,
                          cursor: (!draft.trim() || isTidying || isSaving) ? 'not-allowed' : 'pointer',
                          opacity: (!draft.trim() || isTidying || isSaving) ? 0.5 : 1,
                        }}
                      >
                        {isTidying ? 'Harry tidying…' : 'Tidy with Harry'}
                      </button>
                      <button
                        type="button"
                        onClick={() => saveAsDraft(e.id)}
                        disabled={!draft.trim() || isTidying || isSaving}
                        style={{
                          padding: '7px 14px', borderRadius: 8,
                          border: 'none', background: '#0F1419',
                          color: '#FAFAF8', fontSize: 12.5, fontWeight: 500,
                          cursor: (!draft.trim() || isTidying || isSaving) ? 'not-allowed' : 'pointer',
                          opacity: (!draft.trim() || isTidying || isSaving) ? 0.5 : 1,
                        }}
                        title="Save to Outlook Drafts. You'll review + send from your Outlook drafts folder. Signature is attached."
                      >
                        {isSaving ? 'Saving…' : 'Save as draft'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setOpenId(null); }}
                        disabled={isSaving}
                        style={{
                          marginLeft: 'auto',
                          padding: '7px 12px', borderRadius: 8,
                          border: 'none', background: 'transparent',
                          color: '#8B9094', fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
